import ApiService from '@/services/ApiService';
import Device from '@/wrappers/Device';
import latToCyr from '@/utilities/LatToCyr'
import printJS from 'print-js';
import html2pdf from 'html2pdf.js';
import store from '@/store';

import ElectronPrintService from '@/print/ElectronPrintService';
import AndroidPrintService from '@/print/AndroidPrintService';
import WebPrintService from '@/print/WebPrintService';

import FiscalServiceLegacy from '@/services/FiscalServiceLegacy';

// I18n
import i18n from '@/i18n';

const PRINT_CONFIG_KEY = 'print-config';

export default {
    printConfig: null,
    defaultConfig: {
        local: {
            printLocally: true,
            isPrintServer: false,
            machineIp: null,
            machineId: null,
            printers: {}
        },
        remote: {
            printServerIp: null,
            printServerId: null,
            printers: {}
        }
    },

    service: null,
    device: null,

    detectedPrinters: {
        local: [],
        remote: [],
    },

    /**
     * Initialize Printing Config
     */
    async init() {
        // Initialize the configuration
        await this.initConfiguration()

        // Initialize the printing service
        await this.initService();

        // Detect printers
        await this.detectPrinters();
    },

    /**
     * Initialize the configuration
     */
    async initConfiguration() {
        // Get device info
        this.device = Device.get();
        this.printConfig = await this.getConfig()

        // If no config is present, save the default one
        if (!this.printConfig) {
            this.printConfig = this.defaultConfig;
            this.configure(this.printConfig);
        }

        // TODO remove in the few versions after 5.9.0
        else {
            const storedConfig = await AppStorage.native().getItem(PRINT_CONFIG_KEY)
            if(!storedConfig) {
                AppStorage.native().setItem(PRINT_CONFIG_KEY, JSON.stringify(this.printConfig));
            }
        }

        // In allegra app each device prints locally and there's no print server
        if(isAllegra) {
            if(this.printConfig.local.isPrintServer || !this.printConfig.local.printLocally) {
                this.printConfig.local.isPrintServer = false;
                this.printConfig.local.printLocally = true;
                this.configure(this.printConfig);
            }
        } else {
            // Sync config with server
            await this.syncConfigWithServer();
        }
    },

    /**
     * Initialize the appropriate printing service
     */
    async initService() {
        if(!this.device) {
            this.device = Device.get();
        }

        if (this.device.platform === 'electron') {
            this.service = ElectronPrintService;
        } else if (this.device.platform === 'android') {
            this.service = AndroidPrintService;
        } else if (this.device.platform === 'web') {
            this.service = WebPrintService;
        }

        this.printConfig = await this.service.init(this.printConfig);
        this.configure(this.printConfig);
    },

    /**
     * Configure Printing
     */
    configure(config) {
        if(!config) {
            config = this.defaultConfig;
        }

        this.printConfig = config

        AppStorage.local().setItem(PRINT_CONFIG_KEY, JSON.stringify(config));
        AppStorage.native().setItem(PRINT_CONFIG_KEY, JSON.stringify(config));
    },

    /**
     * Get Printing configuration
     */
    async getConfig() {
        let printConfig = AppStorage.local().getItem(PRINT_CONFIG_KEY)

        if(!printConfig) {
            printConfig = await AppStorage.native().getItem(PRINT_CONFIG_KEY)
        }

        return JSON.parse(printConfig)
    },

    /**
     * Set as main print server
     */
    async setAsMainPrintServer() {
        if(this.device.platform === 'electron' && this.service) {
            this.printConfig = await this.service.setAsMainPrintServer();
            this.configure(this.printConfig)
        }
    },

    /**
     * Sync Configuration with the app server
     */
    async syncConfigWithServer() {
        try {
            // get the config from the server
            const response = await ApiService.get('/print/config')

            this.printConfig.remote.printServerIp = response.data.server.printServerIp;
            this.printConfig.remote.printServerId = response.data.server.printServerId;
            this.printConfig.remote.printers = response.data.printers;
            this.configure(this.printConfig);

            return this.printConfig
        } catch(error) {
            console.log(error.message)
            return this.printConfig
        }
    },

    /**
     * Detect Printers both on the local device
     * and on the print server if set up
     */
    async detectPrinters() {

        // detect local printers
        this.detectedPrinters.local = await this.service.detectPrinters();

        // detect printers on the print server if it's set up
        if(!this.printConfig.remote.printServerId){
            return {
                detectedPrinters: this.detectedPrinters,
                printServerStatus: 'not_set_up'
            };
        }

        const ipAddress = this.printConfig.local.isPrintServer || this.printConfig.local.printLocally
            ? '127.0.0.1'
            : this.printConfig.remote.printServerIp

        try {
            const response = await ApiService.http.get(`http://${ipAddress}:10011/detect`, {
                headers: { 'Content-Type': 'application/json' },
                timeout: 5000
            })

            this.detectedPrinters.remote = response ? response.data : [];
            const printServerStatus = response ? 'connected' : 'not_connected';

            return {
                detectedPrinters: this.detectedPrinters,
                printServerStatus: printServerStatus
            };
        } catch (error) {
            return {
                detectedPrinters: this.detectedPrinters,
                printServerStatus: 'not_connected'
            }
        }
    },

    /**
     * Print order
     *
     * @param {Order} order
     * @param {Object} printer
     */
    printOrder(order, printer = null, printingSector = null) {
        const html = !this.shouldGenerateHtml(printer) ? '' : order.toHtml({
            isReceipt: false,
            isCopy: false,
            printingSector: printingSector,
            forceInvoiceForm: false
        })

        let charsPerLine = printer && printer.options && printer.options.charsPerLine
            ? printer.options.charsPerLine
            : 32;

        let canDoubleFontWidth = this.canDoubleFontWidth(charsPerLine)
        charsPerLine = canDoubleFontWidth ? parseInt(charsPerLine / 2) : charsPerLine

        const text = order.toText({
            isReceipt: false,
            isCopy: false,
            printingSector: printingSector
        }, charsPerLine);

        let highlight = {};
        text.split('\n').forEach((line, index) => {
            let width = canDoubleFontWidth ? 'large' : 'normal'
            let height = line.includes(' x ') || line.includes(' # ') ? 'large' : 'normal'
            highlight[index] = { width: width, height: height, bold: true }
        })

        return this.sendToPrinter(printer, html, text, null, {
            highlight: highlight
        });
    },

    /**
     * Print receipt
     *
     * @param {Order} order
     */
    printReceipt(order, options = {}) {
        const { isCopy, choosePrinter, openDrawer, forceInvoiceForm } = options

        const printer = PrintService.getAssignedPrinter('receipt');
        if(printer?.printer?.name === 'fp1') return Promise.resolve({ success: true });

        const html = !this.shouldGenerateHtml(printer) ? '' : order.toHtml({
            isReceipt: true,
            isCopy: isCopy,
            printingSector: null,
            forceInvoiceForm: forceInvoiceForm
        });

        if(options.saveAsPdfFile) {
            this.generatePdf('print-container', options.saveAsPdfFile);
            return Promise.resolve({ success: true });
        }

        if (choosePrinter || !printer) {
            if(this.device.platform === 'android') {
                return Promise.resolve({ success: false, errorCode: 'receipt_printer_not_set_up' });
            }

            this.printHtml('print-container');
            return Promise.resolve({ success: true });
        }

        const charsPerLine = printer && printer.options && printer.options.charsPerLine
            ? printer.options.charsPerLine
            : 32;

        let text = order.toText({
            isReceipt: true,
            isCopy: isCopy,
            printingSector: null
        }, charsPerLine);

        const qrCode = !order.isProforma() ? order.generateQRCodeString() : null;

        let domain = ''
        if(isAllegra) domain = locale === 'sr' ? 'www.allegrapos.rs' : (locale === 'me' ? 'www.allegrapos.me' : 'www.allegrapos.ba')
        else domain = locale === 'sr' ? 'www.ariapos.rs' : (locale === 'me' ? 'www.ariapos.me' : 'www.ariapos.ba');

        const processed = this.highlightReceipt(text, charsPerLine);

        return this.sendToPrinter(printer, html, processed.text, qrCode, {
            qrCodeEmbed: this.getQrCodeEmbed(order, processed.text),
            openDrawer: openDrawer,
            highlight: processed.highlight,
            footer: domain
        });
    },

    /**
     * Print report
     *
     * @param {*} report
     */
    printReport(report, options = {}) {
        const { choosePrinter } = options

        const printer = PrintService.getAssignedPrinter('receipt');

        let charsPerLine = printer && printer.options && printer.options.charsPerLine
            ? printer.options.charsPerLine
            : 32;

        // TODO temp fix
        if(printer && printer.options && !printer.options.escpos) {
            charsPerLine = 32;
        }

        const html = !this.shouldGenerateHtml(printer) ? '' : report.getPrintable().toHtml('report').join('');

        if (choosePrinter || !printer) {
            if(this.device.platform === 'android') {
                return Promise.resolve({ success: false, errorCode: 'receipt_printer_not_set_up' });
            }

            const printContainer = document.querySelector('#print-container')
            printContainer.innerHTML = html

            this.printHtml('print-container');
            return Promise.resolve({ success: true });
        }

        const text = report.getPrintable().toText(charsPerLine).join('\n');

        return this.sendToPrinter(printer, html, text, null);
    },

    /**
     * Print custom text / html to printer
     */
    printCustom(text, html, qrCode, options = {}) {
        const { choosePrinter } = options

        const isAndroid = this.device.platform === 'android'

        const printer = PrintService.getAssignedPrinter('receipt');

        if (choosePrinter || !printer) {
            if(isAndroid) {
                return Promise.resolve({ success: false, errorCode: 'receipt_printer_not_set_up' });
            }

            const printContainer = document.querySelector('#print-container')
            printContainer.innerHTML = html || text

            this.printHtml('print-container');
            return Promise.resolve({ success: true });
        }

        return this.sendToPrinter(printer, html, text, qrCode);
    },

    /**
     * Open printer cash drawer
     */
    openDrawer() {
        const printer = PrintService.getAssignedPrinter('receipt');

        if(!printer) return

        // If connection is local, use the local print service
        if (printer.connection === 'local') {
            this.service.openDrawer(printer)
            return
        }

        if(!this.printConfig.local.isPrintServer && !this.printConfig.local.printLocally) {
            return
        }

        ApiService.http.post(
            'http://127.0.0.1:10011/drawer',
            { printer: printer },
            { headers: { 'Content-Type': 'application/json' } }
        )
    },

    /**
     * Send to printer
     *
     * @param printer
     * @param html
     * @param text
     * @param qrCode
     * @param options
     */
    async sendToPrinter(printer, html, text, qrCode, options = {}) {
        text = this.sanitizeText(text)
        options.codepage = !!store.getters.tenant?.venue?.settings?.cyrilic_receipt ? 'windows1251' : 'windows1250';

        if(printer?.printer?.name === 'fp1') {
            const result = await FiscalServiceLegacy.printText('\n' + text + '\n')
            if(result.error) {
                return { success: false, errorCode: 'receipt_printing_failed' }
            }
            return { success: true }
        }

        // If connection is local, use the local print service
        if (printer && printer.connection === 'local') {
            if(!this.service) {
                this.initConfiguration()
                await this.initService()
            }
            const result = await this.service.sendToPrinter(printer, html, text, qrCode, options)
            return result
        }

        // If connection is remote, print trough the main printing device
        const ipAddress = this.printConfig.local.isPrintServer || this.printConfig.local.printLocally
            ? '127.0.0.1'
            : this.printConfig.remote.printServerIp

        try {
            const response = await ApiService.http.post(
                `http://${ipAddress}:10011/print`,
                {
                    printer: printer,
                    data: {
                        html: html,
                        text: text,
                        qrCode: qrCode,
                        qrCodeEmbed: options.qrCodeEmbed,
                        openDrawer: !!options.openDrawer,
                        highlight: options.highlight,
                        footer: options.footer,
                        codepage: options.codepage,
                        remotePrint: ipAddress !== '127.0.0.1'
                    }
                },
                { headers: { 'Content-Type': 'application/json' } }
            )

            if(!response.data.success){
                return { success: false, errorCode: 'main_device_print_failed' }
            }

            return { success: true }
        } catch (error) {
            return { success: false, errorCode: 'main_device_connection_failed' };
        }
    },

    /**
     * Print HTML element
     */
    printHtml(element) {
        printJS({
            printable: element,
            type: 'html',
            css: '/print/print.css',
            scanStyles: false,
        });
    },

    /**
     * Print HTML element
     */
    async generatePdf(element, fileName, save = true) {
        const printContainer = document.getElementById(element)

        const options = {
            margin: 5,
            filename: fileName,
            html2canvas: { scale: 10 },
            jsPDF: { unit: 'mm', format: 'A4', orientation: 'portrait' }
        };

        if(!save) {
            return await html2pdf().set(options).from(printContainer).output('datauristring');
        }

        html2pdf().set(options).from(printContainer).save();
    },

    /**
     * Get the printer assigned to a specific role (eg. receipt, order, food)
     */
    getAssignedPrinter(role){
        if(this.device.platform === 'web') {
            return null;
        }

        if(this.printConfig.local.printLocally) {
            const localPrinter = this.printConfig.local.printers[role];
            if(!localPrinter) {
                return null
            }

            return {
                ...localPrinter,
                connection: 'local',
            };
        }

        const remotePrinter = this.printConfig.remote.printers[role];

        if(!remotePrinter) {
            return null
        }

        return {
            ...remotePrinter,
            connection: 'remote',
        }
    },

    canDoubleFontWidth(charsPerLine) {
        if(this.device.platform === 'electron' && charsPerLine >= 40) {
            return true
        }

        return false
    },

    /**
     * highlight to pay line and not a fiscal receipt lines
     */
    highlightReceipt(text, charsPerLine) {
        let canDoubleFontWidth = this.canDoubleFontWidth(charsPerLine)

        const textLines = text.split('\n')

        const toPayLineIndex = textLines.findIndex(line => {
            return line.toLowerCase().includes(i18n.t('general.to_pay').toLowerCase())
        })

        const notFiscalReceiptLineIndexes = textLines.map((line, index) => {
            return line.toLowerCase().includes(latToCyr(i18n.t('receipt.nonfiscal_receipt')).toLowerCase()) ? index : null
        }).filter(index => index !== null)

        if(toPayLineIndex === -1 && !notFiscalReceiptLineIndexes.length) {
            return { text, highlight: null }
        }

        let highlight = {}

        if(toPayLineIndex !== -1) {
            if(canDoubleFontWidth) {
                textLines[toPayLineIndex] = textLines[toPayLineIndex]
                    .replace(Array(parseInt(charsPerLine / 2)).fill(' ').join(''), '')
                text = textLines.join('\n')
            }

            highlight[toPayLineIndex] = { width: canDoubleFontWidth ? 'large' : 'normal', height: 'large', bold: false }
        }

        notFiscalReceiptLineIndexes.forEach(index => {
            highlight[index] = { width: 'normal', height: 'large', bold: false }
        })

        return { text, highlight };
    },

    getQrCodeEmbed(order, text = null) {
        if(locale !== 'sr') return 0;

        const textLines = text ? text.split('\n') : order.toText({ isReceipt: true }).split('\n');

        let endLineIndex = textLines.length - 1;
        textLines.forEach((line, index) => {
            if(
                line.includes(latToCyr(i18n.t('receipt.fiscal_receipt_end'))) || line.includes(latToCyr(i18n.t('receipt.nonfiscal_receipt_end'))) ||
                line.includes(i18n.t('receipt.fiscal_receipt_end')) || line.includes(i18n.t('receipt.nonfiscal_receipt_end'))
            ) {
                endLineIndex = index;
            }
        })

        let extraNumLines = 0;
        const invoice = order.register_request || order.getRegisterRequestSr();
        if(invoice.invoiceType === 'Copy' && invoice.transactionType === 'Refund') {
            extraNumLines += 3;
        }

        return textLines.length - endLineIndex + extraNumLines;
    },

    shouldGenerateHtml(printer) {
        if(this.device?.platform === 'android' && printer?.connection === 'local') {
            return false
        }

        return true
    },

    sanitizeText(text) {
        return text.replace(/č/g, 'c')
            .replace(/Č/g, 'C')
            .replace(/ć/g, 'c')
            .replace(/Ć/g, 'C')
            .replace(/ž/g, 'z')
            .replace(/Ž/g, 'Z')
            .replace(/đ/g, 'dj')
            .replace(/Đ/g, 'Dj')
            .replace(/š/g, 's')
            .replace(/Š/g, 'S')
    }
};
